import { ref } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';

export default function useCities() {
  /** @type {import('vue').Ref<Array<{id: string, name: string, code: string}>>} */
  const cityList = ref([]);

  /** @type {import('vue').Ref<Error|null>} */
  const fetchError = ref(null);

  /** @type {import('vue').Ref<boolean>} */
  const googleMapsLoaded = ref(false);

  /** @type {Loader|null} */
  let loaderInstance = null;

  /**
   * Initialize the Google Maps API loader once
   * @returns {Loader}
   */
  const initGoogleMapsLoader = () => {
    if (!loaderInstance) {
      loaderInstance = new Loader({
        apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        language: 'en',
        version: 'weekly',
        authReferrerPolicy: 'origin',
        libraries: ['places'],
      });
    }
    return loaderInstance;
  };

  /**
   * Clears the city list and resets error state
   */
  const clearCityList = () => {
    cityList.value = [];
    fetchError.value = null;
  };

  /**
   * Fetch city predictions using the Google Places Autocomplete API
   * @param {{ code: string }} selectedCountry - The selected country object with a country code
   * @param {string} inputCity - The user-inputted city name
   * @returns {Promise<void>}
   */
  const fetchCities = async (selectedCountry, inputCity) => {
    clearCityList();

    if (!inputCity || inputCity.length < 2 || !selectedCountry?.code) {
      return;
    }

    try {
      const loader = initGoogleMapsLoader();
      const { AutocompleteSuggestion, AutocompleteSessionToken } =
        await loader.importLibrary('places');

      googleMapsLoaded.value = true;

      const sessionToken = new AutocompleteSessionToken();
      const request = {
        input: inputCity,
        includedRegionCodes: [selectedCountry.code],
        includedPrimaryTypes: ['political'],
        language: 'en',
        sessionToken,
      };

      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const safeSuggestions = Array.isArray(suggestions) ? suggestions : [];
      const uniqueCities = Array.from(
        new Set(
          safeSuggestions.map((s) => s.placePrediction.mainText.toString())
        )
      );

      cityList.value = uniqueCities.map((city) => ({
        id: city,
        name: city,
        code: city,
      }));
    } catch (error) {
      fetchError.value =
        error instanceof Error ? error : new Error('Unknown error');
      googleMapsLoaded.value = false;
      console.error('City fetch failed:', error);
    }
  };

  return {
    cityList,
    clearCityList,
    fetchCities,
    googleMapsLoaded,
    fetchError,
  };
}
